import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMacro } from "../lib/api";
import { TrendingUp, AlertCircle, Compass } from "lucide-react";

const phaseColor = (p) => ({
    ESPANSIONE: "var(--green)",
    RIPRESA: "var(--cyan)",
    REFLAZIONE: "var(--amber)",
    RALLENTAMENTO: "var(--yellow)",
    RECESSIONE: "var(--red)",
    STAGFLAZIONE: "var(--magenta)",
}[p] || "var(--amber)");

const REGION_META = {
    us: { label: "Stati Uniti", flag: "🇺🇸", short: "USA" },
    eu: { label: "Europa", flag: "🇪🇺", short: "EUR" },
};

export default function Macro() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [region, setRegion] = useState("us");
    const nav = useNavigate();

    useEffect(() => {
        getMacro().then(setData).catch(() => {}).finally(() => setLoading(false));
    }, []);

    // Pick the regional payload from the new API shape; fall back to the legacy
    // top-level snapshot/analysis fields so the page still renders for any old
    // cached responses or unmigrated clients.
    const regional = useMemo(() => {
        if (!data) return null;
        const r = data.regions?.[region];
        if (r) return r;
        if (region === "us") return { snapshot: data.snapshot, analysis: data.analysis };
        return null;
    }, [data, region]);

    const snapshotEntries = useMemo(
        () => (regional?.snapshot ? Object.entries(regional.snapshot).filter(([, v]) => typeof v === "object") : []),
        [regional]
    );

    if (loading || !data) {
        return <div className="px-4 py-20 text-center text-mute">Analisi macro in corso (AI Claude Sonnet 4.5)…</div>;
    }

    const a = regional?.analysis || {};
    const s = regional?.snapshot || {};
    const color = phaseColor(a.phase);
    const availableRegions = Object.keys(data.regions || { us: 1 });

    return (
        <div data-testid="macro-page" className="px-4 py-6 max-w-[1600px] mx-auto fade-up">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[10px] text-mute tracking-[0.2em] mb-1">MACROECONOMIC CYCLE ANALYSIS / AI POWERED</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight brand-gradient-text">
                        Ciclo economico
                    </h1>
                </div>
                {availableRegions.length > 1 && (
                    <div data-testid="region-tabs" className="flex border border-soft">
                        {availableRegions.map(r => {
                            const meta = REGION_META[r] || { label: r.toUpperCase(), flag: "" };
                            const active = r === region;
                            return (
                                <button
                                    key={r}
                                    data-testid={`region-tab-${r}`}
                                    onClick={() => setRegion(r)}
                                    className="px-4 py-2 text-xs font-semibold tracking-wider transition-colors"
                                    style={{
                                        background: active ? "var(--brand-blue)" : "transparent",
                                        color: active ? "#000" : "var(--text-dim)",
                                    }}
                                >
                                    {meta.flag} {meta.label}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Phase card */}
                <div data-testid="phase-card" className="lg:col-span-2 border border-soft bg-elev p-6 relative overflow-hidden">
                    <div className="grid-bg absolute inset-0 opacity-30 pointer-events-none"></div>
                    <div className="relative">
                        <div className="flex items-center gap-2 text-xs text-mute tracking-widest mb-2">
                            <Compass size={14} /> FASE DEL CICLO
                        </div>
                        <div className="font-display text-5xl sm:text-6xl font-bold mb-3" style={{ color }}>
                            {a.phase}
                        </div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="text-xs text-mute">CONFIDENCE</div>
                            <div className="flex-1 max-w-xs h-1.5 bg-elev2 relative">
                                <div className="absolute inset-y-0 left-0" style={{ width: `${a.confidence}%`, background: color }}></div>
                            </div>
                            <div className="tnum font-bold">{a.confidence}%</div>
                        </div>
                        <p className="text-sm text-dim max-w-3xl leading-relaxed">{a.summary}</p>

                        <div className="mt-5">
                            <div className="text-[10px] text-mute tracking-widest mb-2">KEY DRIVERS</div>
                            <div className="flex flex-wrap gap-2">
                                {(a.key_drivers || []).map((d) => (
                                    <span key={d} className="border border-soft bg-elev2 px-2 py-1 text-xs text-dim">{d}</span>
                                ))}
                            </div>
                        </div>

                        <div className="mt-5 border-t border-soft pt-4">
                            <div className="text-[10px] text-mute tracking-widest mb-2">OUTLOOK 3M</div>
                            <p className="text-sm text-dim">{a.outlook_3m}</p>
                        </div>
                    </div>
                </div>

                {/* Macro snapshot */}
                <div className="border border-soft bg-elev p-4">
                    <div className="text-xs text-mute tracking-wider mb-3">
                        MACRO SNAPSHOT · {REGION_META[region]?.short || region.toUpperCase()}
                    </div>
                    <div className="space-y-2">
                        {snapshotEntries.map(([k, v]) => (
                            <div key={k} className="flex justify-between items-center text-xs py-1.5 border-b border-soft">
                                <div>
                                    <div className="uppercase tracking-wider">{k}</div>
                                    <div className="text-mute text-[10px]">{v.symbol}</div>
                                </div>
                                <div className="text-right">
                                    <div className="tnum font-bold">{v.value}</div>
                                    <div className={`text-[10px] tnum ${(v.change_3m_pct || 0) >= 0 ? "text-green-up" : "text-red-down"}`}>
                                        {(v.change_3m_pct || 0) >= 0 ? "+" : ""}{v.change_3m_pct}% 3M
                                    </div>
                                </div>
                            </div>
                        ))}
                        {s.yield_curve_spread !== undefined && (
                            <div className="flex justify-between items-center text-xs py-1.5 pt-3">
                                <div className="uppercase tracking-wider">Yield Curve 10Y-2Y</div>
                                <div className={`tnum font-bold ${s.yield_curve_spread < 0 ? "text-red-down" : "text-green-up"}`}>
                                    {s.yield_curve_spread}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Best / Avoid assets */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div data-testid="best-assets-card" className="border border-soft bg-elev p-5">
                    <div className="flex items-center gap-2 text-xs text-mute tracking-widest mb-4">
                        <TrendingUp size={14} style={{ color: "var(--green)" }} /> ASSET DA PREFERIRE
                    </div>
                    <div className="space-y-4">
                        {(a.best_assets || []).map((ba) => (
                            <div key={ba.category} className="border-l-2 pl-3" style={{ borderColor: "var(--green)" }}>
                                <div className="font-semibold text-sm" style={{ color: "var(--green)" }}>{ba.category}</div>
                                <div className="flex flex-wrap gap-1.5 my-1.5">
                                    {(ba.symbols || []).map(sy => (
                                        <button
                                            key={sy}
                                            data-testid={`best-symbol-${sy}`}
                                            onClick={() => nav(`/asset/${encodeURIComponent(sy)}`)}
                                            className="text-[11px] tnum border border-soft bg-elev2 px-2 py-0.5 hover:border-amber hover:text-amber"
                                        >
                                            {sy}
                                        </button>
                                    ))}
                                </div>
                                <div className="text-xs text-dim">{ba.rationale}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div data-testid="avoid-assets-card" className="border border-soft bg-elev p-5">
                    <div className="flex items-center gap-2 text-xs text-mute tracking-widest mb-4">
                        <AlertCircle size={14} style={{ color: "var(--red)" }} /> ASSET DA EVITARE
                    </div>
                    <div className="space-y-4">
                        {(a.avoid_assets || []).map((av) => (
                            <div key={av.category} className="border-l-2 pl-3" style={{ borderColor: "var(--red)" }}>
                                <div className="font-semibold text-sm" style={{ color: "var(--red)" }}>{av.category}</div>
                                <div className="text-xs text-dim mt-1">{av.rationale}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
